// Часть разбиения MapView.vue (фаза 2) - маркеры узлов (камера+станция на
// хабе) и их попапы, плюс одноразовое начальное центрирование по ближайшему
// узлу (maybeCenterInitially).
import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import api, { resolveMediaUrl } from "../api";
import type { MapNode } from "../types/nodes";
import type { MapCtx } from "../map/mapContext";
import {
  escapeHtml,
  formatLocalDateTime,
  formatMetric,
  formatPercent,
  markerElement,
  PIN_SIZE,
  setMarkerVisible,
  shouldShowDetailMarkers,
  upsertPopupContent,
} from "../map/mapUiHelpers";
import hubIconUrl from "../assets/hub-icon.png";
import { safeStringify } from "./useMapDebugPanel";
import { nodePinCaption } from "../utils/nodePinCaption";

export interface UseNodeMarkersOptions {
  // maybeCenterInitially пропускается целиком, если маршрут уже выбран через
  // URL при загрузке - диплинк на маршрут держит фокус на его стартовой точке.
  hasSelectedRouteId: () => boolean;
  // Единица ветра для подписи под пином (i18n units.ms) - композабл сам
  // переводов не знает.
  windUnit: () => string;
}

export function useNodeMarkers(ctx: MapCtx, options: UseNodeMarkersOptions) {
  const nodeMarkers = new Map<number, Marker>();

  // Резолвится при первом завершении fetchNodes() (успех или ошибка) - даёт
  // начальному центрированию по геолокации (maybeCenterInitially) дождаться
  // реальных координат узлов, не гонясь с асинхронным запросом /nodes.
  let nodesReadyResolve: (() => void) | null = null;
  const nodesReadyPromise = new Promise<void>((resolve) => {
    nodesReadyResolve = resolve;
  });

  let hasCenteredInitially = false;
  // Пользователь мог успеть подвигать карту сам, пока геолокация/список
  // узлов ещё грузятся (оба - сетевые запросы, не мгновенные) - тогда
  // maybeCenterInitially не должен перебивать его прыжком к узлу поверх
  // того, куда он уже перешёл. e?.originalEvent есть только у настоящего
  // жеста (drag/wheel/touch), не у программных jumpTo/easeTo - тот же
  // паттерн, что handleRotateStart в useMapRotation3D.ts.
  let userInteractedBeforeCenter = false;
  const markMapInteraction = (e?: { originalEvent?: unknown }) => {
    if (e?.originalEvent) userInteractedBeforeCenter = true;
  };

  // Векторный пин-капля с фото хаба, обрезанным в круглую голову. Цвет пина
  // настраивается через --node-pin-color на .node-map-pin.
  const createNodeElement = (nodeId: number, label: string, caption: string | null) =>
    markerElement(
      "node-marker-icon",
      `
      <div class="node-map-pin" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">
        <svg viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <clipPath id="node-pin-clip-${nodeId}">
              <circle cx="192" cy="192" r="132" />
            </clipPath>
          </defs>
          <path class="node-map-pin__body" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z" />
          <circle class="node-map-pin__hole" cx="192" cy="192" r="144" />
          <image href="${hubIconUrl}" x="32" y="32" width="320" height="320" preserveAspectRatio="xMidYMid meet" clip-path="url(#node-pin-clip-${nodeId})" />
        </svg>
        <span class="node-map-pin__caption"${caption ? "" : " hidden"}>${escapeHtml(caption ?? "")}</span>
      </div>
    `,
      PIN_SIZE
    );

  const nodeGradient = (id: number) => {
    const hues = [210, 200, 158, 185, 170, 195];
    const h = hues[id % hues.length];
    return `linear-gradient(135deg, hsl(${h} 45% 30%), hsl(${h} 50% 45%))`;
  };

  const makeNodePopupHtml = (node: MapNode) => {
    const previewCamera = node.cameras.find((c) => c.is_preview) || node.cameras[0] || null;
    const merged = Boolean(previewCamera && node.station);
    const snapshotImg = previewCamera?.preview_url
      ? `<img src="${escapeHtml(resolveMediaUrl(previewCamera.preview_url) || "")}" alt="" class="node-popup__snapshot" />`
      : "";
    const previewBlock = previewCamera
      ? `
      <div class="node-popup__preview${merged ? " node-popup__preview--merged" : ""}" style="background:${nodeGradient(node.id)}">
        ${snapshotImg}
        <span class="node-popup__live-badge">
          <span class="node-popup__live-dot"></span>${escapeHtml(ctx.t("camera.live"))}
        </span>
        <span class="node-popup__play">▶</span>
        <div class="node-popup__camera-scrim"></div>
        <p class="node-popup__camera-name">${escapeHtml(previewCamera.name)}</p>
      </div>
    `
      : "";

    const latest = node.station?.latest;
    const weatherBlock = node.station
      ? `
      <div class="node-popup__weather${merged ? " node-popup__weather--merged" : ""}">
        <div class="node-popup__weather-row">
          <span class="node-popup__weather-stat node-popup__weather-stat--main">🌡️ ${formatMetric(latest?.temp_c, "°C")}</span>
          <span class="node-popup__weather-stat">💧 ${formatPercent(latest?.humidity_pct)}</span>
          <span class="node-popup__weather-stat">🌬️ ${formatMetric(latest?.wind_speed_ms, " м/с")}</span>
        </div>
        <p class="node-popup__weather-footer">Обновлено: ${formatLocalDateTime(latest?.polled_at ?? undefined)}</p>
      </div>
    `
      : "";

    return `
    <div class="node-popup" data-node-id="${node.id}">
      <p class="node-popup__title">${escapeHtml(node.name)}</p>
      ${previewBlock}
      ${weatherBlock}
      <button
        type="button"
        class="node-popup__live-btn"
        data-node-live="${node.id}"
        ${previewCamera ? `data-camera-id="${previewCamera.id}"` : ""}
      >
        ${escapeHtml(ctx.t("camera.watchStream"))}
      </button>
    </div>
  `;
  };

  const shouldShowDetail = () => shouldShowDetailMarkers(ctx.getMap());

  const applyNodeZoomVisibility = () => {
    const visible = shouldShowDetail();
    nodeMarkers.forEach((marker) => setMarkerVisible(marker, visible));
  };

  const fetchNodes = async () => {
    if (!ctx.isMapReady()) return;
    const map = ctx.getMap();
    try {
      const res = await api.get<MapNode[]>("/nodes");
      const seen = new Set<number>();
      (res.data || []).forEach((node) => {
        seen.add(node.id);
        const caption = nodePinCaption(node, Date.now(), options.windUnit());
        const existing = nodeMarkers.get(node.id);
        if (existing) {
          upsertPopupContent(existing, makeNodePopupHtml(node), ctx);
          const captionEl = existing.getElement().querySelector<HTMLElement>(".node-map-pin__caption");
          if (captionEl) {
            captionEl.textContent = caption ?? "";
            captionEl.hidden = !caption;
          }
          return;
        }
        const marker = new maplibregl.Marker({
          element: createNodeElement(node.id, node.name, caption),
          anchor: "bottom",
        }).setLngLat([node.longitude, node.latitude]);
        upsertPopupContent(marker, makeNodePopupHtml(node), ctx);
        marker.addTo(map!);
        nodeMarkers.set(node.id, marker);
      });
      nodeMarkers.forEach((marker, id) => {
        if (!seen.has(id)) {
          marker.remove();
          nodeMarkers.delete(id);
        }
      });
      applyNodeZoomVisibility();
    } catch (error) {
      ctx.pushDebug("fetch nodes failed", safeStringify(error));
    } finally {
      nodesReadyResolve?.();
      nodesReadyResolve = null;
    }
  };

  // Находит ближайший к позиции узел - для начального центрирования карты
  // (центрируем на ближайшем узле, а не буквально на пользователе, чтобы
  // первым делом было видно что-то реально относящееся к карте).
  const findNearestNodeTo = (lng: number, lat: number): [number, number] | null => {
    let nearest: [number, number] | null = null;
    let minDistance = Infinity;
    const from = new maplibregl.LngLat(lng, lat);
    nodeMarkers.forEach((marker) => {
      const candidate = marker.getLngLat();
      const distance = candidate.distanceTo(from);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = [candidate.lng, candidate.lat];
      }
    });
    return nearest;
  };

  // Срабатывает один раз, на первый фикс позиции (GPS или IP) после создания
  // карты - находит ближайший узел и центрирует на нём. Полностью пропускается,
  // если маршрут уже выбран через URL при загрузке (tryInitMap/loadSelectedRoute),
  // чтобы диплинк на маршрут держал фокус на его собственной стартовой точке.
  // Больше никогда не срабатывает, так что переключение вкладок не вызывает
  // повторный скачок.
  const maybeCenterInitially = (lng: number, lat: number) => {
    if (hasCenteredInitially) return;
    hasCenteredInitially = true;
    if (options.hasSelectedRouteId()) return;
    nodesReadyPromise.then(() => {
      if (userInteractedBeforeCenter) return;
      const map = ctx.getMap();
      if (!map) return;
      map.jumpTo({ center: findNearestNodeTo(lng, lat) ?? [lng, lat], zoom: 14 });
    });
  };

  return {
    nodeMarkers,
    fetchNodes,
    applyNodeZoomVisibility,
    maybeCenterInitially,
    markMapInteraction,
  };
}
